import { AccessLevelEnum } from '@zaoblako/shared/modules/security/enums/access.level.enum';
import type { ApplicationModuleFullFindInterface } from '@zaoblako/shared/modules/security/dtos/application/module/application-module-full-find.dto';
import type { ApplicationAccessControlFullFindInterface } from '@zaoblako/shared/modules/security/dtos/application/access-control/application-access-control-full-find.dto';
import type { ApplicationAccessControlRatePlanFindInterface } from '@zaoblako/shared/modules/security/dtos/application/access-control/application-access-control-rate-plan-find.dto';
import type { ApplicationModuleBaseInterface } from '@zaoblako/shared/modules/security/dtos/application/module/application-module-base.dto';

/** "Basis" module reference object */
const BASIS_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'Базовый',
  description: 'Базовый функционал, без которого невозможно полноценно работать с системой',
  enabled: true,
  dependencies: [],
  sections: {
    SETTINGS: {
      displayName: 'Общие настройки',
      isAdmin: false,
      permissions: {},
      tabs: {
        SETTINGS: {
          displayName: 'Общие настройки платежного аккаунта',
          accessLevel: AccessLevelEnum.READ,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          editable: false,
          enabled: true,
          permissions: {},
        },
      },
    },
    DASHBOARD: {
      displayName: 'Рабочий стол',
      isAdmin: false,
      permissions: {},
      tabs: {
        TEMPLATE: {
          displayName: 'Шаблоны вкладок',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        ACCOUNTS_MONEY: {
          displayName: 'Виджет "Деньги на счетах"',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        CASHES_MONEY: {
          displayName: 'Виджет "Деньги в кассе"',
          accessLevel: AccessLevelEnum.FORBIDDEN,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          editable: false,
          enabled: true,
          permissions: {},
        },
        VAT: {
          displayName: 'Виджет "НДС"',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
    },
    COMPANY: {
      displayName: 'Компании',
      isAdmin: false,
      tabs: {
        COMPANY: {
          displayName: 'Компании',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        COMPANY_GROUP: {
          displayName: 'Группы компаний',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        ERP_1C: {
          displayName: 'Интеграция с 1С',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
      },
      permissions: {
        ACCESS_TO_ALL_COMPANIES: {
          displayName: 'Доступ ко всем компаниям',
          isAllowed: false,
        },
      },
    },
    CLIENT: {
      displayName: 'Клиенты',
      isAdmin: false,
      tabs: {
        CLIENT: {
          displayName: 'Клиенты',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        CONTRAGENT: {
          displayName: 'Контрагенты',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        CONTRACT: {
          displayName: 'Договоры',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        EXTERNAL_USER: {
          displayName: 'Внешние пользователи',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        BANNER: {
          displayName: 'Баннеры ЛК',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
      },
      permissions: {
        CAN_DELETE_CLIENTS: {
          displayName: 'Можно удалять клиентов',
          isAllowed: false,
        },
      },
    },
    SECURITY: {
      displayName: 'Пользователи',
      isAdmin: false,
      tabs: {
        USER: {
          displayName: 'Сотрудники',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {
            ACCESS_TO_LIST: {
              displayName: 'Доступ к списку',
              isAllowed: true,
            },
          },
        },
        GROUP: {
          displayName: 'Группы',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {
            ACCESS_TO_LIST: {
              displayName: 'Доступ к списку',
              isAllowed: true,
            },
          },
        },
        ROLE: {
          displayName: 'Роли',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        WORK_SCHEDULE: {
          displayName: 'Графики работы',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        UNREAD_REPORT: {
          displayName: 'Непрочитанные уведомления',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        CAN_BAN_USER: {
          displayName: 'Блокировка пользователей',
          isAllowed: false,
        },
      },
    },
    CATALOG: {
      displayName: 'Справочники',
      isAdmin: false,
      tabs: {
        DIRECTION: {
          displayName: 'Бюджетные статьи (БС)',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        UNIT: {
          displayName: 'Бизнес-единицы (БЕ)',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        FILIAL: {
          displayName: 'Филиалы',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        ROOM: {
          displayName: 'Помещения',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        DOCUMENT_TYPE: {
          displayName: 'Типы документов',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        OPERATION_TYPE: {
          displayName: 'Виды операций',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        MASTER_MILESTONE: {
          displayName: 'Эпики',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        APPROVAL_SCHEME: {
          displayName: 'Схемы согласования',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        NOMENCLATURE: {
          displayName: 'Номенклатура',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        COST_CENTER: {
          displayName: 'Центр затрат (ЦЗ)',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        ACCOUNTING_ACCOUNT: {
          displayName: 'Бухгалтерские счета',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        AI: {
          displayName: 'Искусственный интеллект',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        WEBHOOK: {
          displayName: 'Веб-хуки',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        DOCUMENT_FILE_TEMPLATE: {
          displayName: 'Шаблоны документов (Word)',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        EAV_ENTITY_TYPE: {
          displayName: 'Шаблоны полей',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
      },
      permissions: {
        ACCESS_TO_ALL_DIRECTIONS: {
          displayName: 'Доступ ко всем БС',
          isAllowed: false,
        },
        ACCESS_TO_ALL_UNITS: {
          displayName: 'Доступ ко всем БЕ',
          isAllowed: false,
        },
        ACCESS_TO_ALL_APPROVAL_SCHEMES: {
          displayName: 'Доступ ко всем схемам согласования',
          isAllowed: false,
        },
      },
    },
    SYSTEM: {
      displayName: 'Админ панель',
      isAdmin: true,
      tabs: {
        TENANT: {
          displayName: 'Тенанты',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        PLUGIN_1C: {
          displayName: 'Расширения 1С',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        CHANGELOG: {
          displayName: 'История версий',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        RATE_PLAN: {
          displayName: 'Тарифы',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        BUDGET_CLASSIFICATION_CODE: {
          displayName: 'КБК',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        PAYER_STATUS: {
          displayName: 'Статусы плательщика',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        CURRENCY: {
          displayName: 'Валюты',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        BULL_MQ: {
          displayName: 'BullMQ',
          accessLevel: AccessLevelEnum.FORBIDDEN,
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          permissions: {},
        },
      },
      permissions: {},
    },
  },
};

/** "Finance" module reference object */
const FINANCE_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'Финансы',
  description: 'Управление финансами - доходы и расходы',
  enabled: false,
  dependencies: [],
  sections: {
    PLAN_FACT: {
      displayName: 'Бюджетирование',
      isAdmin: false,
      tabs: {
        BALANCE_REPORT: {
          displayName: 'Отчет о прибылях и убытках (ОПиУ)',
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          editable: false,
          enabled: true,
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        SETTINGS: {
          displayName: 'Настройки',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        PAYMENT_OUTGOING: {
          displayName: 'Анализ расходов и доходов',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        BUDGET_REQUEST: {
          displayName: 'Заявка на бюджет',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        BALANCE_SHEET: {
          displayName: 'Загрузка данных по ОСВ',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        PLANNING_SCENARIO: {
          displayName: 'Сценарии планирования',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        ACCESS_TO_ALL_BUDGET_REQUESTS: {
          displayName: 'Доступ ко всем заявкам на бюджет',
          isAllowed: false,
        },
        EDIT_ALL_BUDGET_REQUESTS: {
          displayName: 'Редактирование всех заявок на бюджет',
          isAllowed: false,
        },
        DELETE_ALL_BUDGET_REQUESTS_COMMENTS: {
          displayName: 'Удаление всех комментариев в заявках на бюджет',
          isAllowed: false,
        },
        CAN_APPROVE_BUDGET_REQUEST_INSTEAD_OF_ANOTHER: {
          displayName: 'Согласование заявок на бюджет вместо других',
          isAllowed: false,
        },
        EDIT_DOCUMENT_RESTRICT_DATE: {
          displayName: 'Редактирование даты запрета на изменение документов',
          isAllowed: false,
        },
        EDIT_PREVIOUS_PLAN: {
          displayName: 'Редактирование плана за предыдушие периоды',
          isAllowed: false,
        },
        ACCESS_TO_ALL_SAVED_FILTERS: {
          displayName: 'Доступ ко всем сохраненными фильтрам',
          isAllowed: false,
        },
      },
    },
    DOCUMENT: {
      displayName: 'Документы',
      isAdmin: false,
      tabs: {
        DOCUMENT_REGISTRY: {
          displayName: 'Реестр документов',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        TEMPLATE: {
          displayName: 'Шаблоны',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        VERIFY: {
          displayName: 'Сверка',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        BANK_STATEMENT_RULE: {
          displayName: 'Правила',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        ABC_ANALYSIS: {
          displayName: 'ABC-анализ',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        SALARY_ACCRUAL: {
          displayName: 'ЗП',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        SALARY_DISTRIBUTION: {
          displayName: 'Распределение ЗП',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        ANALYTICS_RESET: {
          displayName: 'Сброс аналитики',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        CAN_LOCK_DOCUMENTS: {
          displayName: 'Может блокировать новые изменения в документах"',
          isAllowed: false,
        },
        EDIT_OTHER_USER_DOCUMENTS: {
          displayName: 'Редактирование документов других пользователей',
          isAllowed: false,
        },
        ACCESS_TO_ALL_SAVED_FILTERS: {
          displayName: 'Доступ ко всем сохраненными фильтрам',
          isAllowed: false,
        },
      },
    },
  },
};

const BRS_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'Платежи',
  description: 'Контроль платежей',
  enabled: false,
  dependencies: ['FINANCE'],
  sections: {
    BRS: {
      displayName: 'Платежи',
      isAdmin: false,
      tabs: {
        TASK: {
          displayName: 'Заявки на платёж',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        PAYMENT_SCENARION: {
          displayName: 'Сценарии',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        DDS: {
          displayName: 'Согласование платежей',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        PAYMENT_CALENDAR: {
          displayName: 'Платёжный календарь',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        TEMPLATE: {
          displayName: 'Шаблоны заявок на платеж',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        STATUS: {
          displayName: 'Статусы заявок на платеж',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        SETTINGS: {
          displayName: 'Настройки',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        READ_ALL_TASKS: {
          displayName: 'Просмотр любых заявок на платеж',
          isAllowed: false,
        },
        EDIT_OTHER_USERS_TASKS: {
          displayName: 'Редактирование и удаление чужих заявок СБР',
          isAllowed: false,
        },
        APPROVE_INSTEAD_OF_OTHERS: {
          displayName: 'Согласование заявок на платеж от имени других пользователей',
          isAllowed: false,
        },
        DELETE_OTHER_USER_TASK_COMMENTS: {
          displayName: 'Удаление любых комментариев к платежам',
          isAllowed: false,
        },
        REFUSE_AGGREED_AND_SENT_TASKS: {
          displayName: 'Переводить в статус "Отказан" заявки на платеж, которые уже согласованы, и у которых есть платежные поручения в статусе "Отправлено"',
          isAllowed: false,
        },
        MAKE_PAID_UNDERPAYMENT_TASKS: {
          displayName: 'Переводить заявки на платеж из статуса "Недоплата" в статус "Оплачен"',
          isAllowed: false,
        },
        READ_ALL_PLANNED_INCOMES: {
          displayName: 'Просмотр любых планируемых поступлений',
          isAllowed: false,
        },
        EDIT_OTHER_USERS_PLANNED_INCOMES: {
          displayName: 'Редактирование и удаление чужих планируемых поступлений',
          isAllowed: false,
        },
        DELETE_OTHER_USER_PLANNED_INCOME_COMMENTS: {
          displayName: 'Удаление любых комментариев в планируемых поступлениях',
          isAllowed: false,
        },
        ACCESS_TO_ALL_SAVED_FILTERS: {
          displayName: 'Доступ ко всем сохраненными фильтрам',
          isAllowed: false,
        },
      },
    },
  },
};

const BUSINESS_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'Сделки',
  description: 'Сделки и группы сделок',
  enabled: false,
  dependencies: ['FINANCE'],
  sections: {
    BUSINESS: {
      displayName: 'Проекты',
      isAdmin: false,
      tabs: {
        DEAL: {
          displayName: 'Сделки',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        DEAL_TYPE: {
          displayName: 'Типы сделок',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        STATUS: {
          displayName: 'Статусы сделок',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        RENTAL_RATES: {
          displayName: 'Арендные ставки',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        INTEGRATION_MEGAPLAN: {
          displayName: 'Интеграция с "МегаПлан"',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        INTEGRATION_AMO: {
          displayName: 'Интеграция с "amoCRMs"',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        READ_ALL_DEALS: {
          displayName: 'Просмотр любых сделок и проектов',
          isAllowed: false,
        },
        DELETE_OTHER_USER_DEAL_COMMENTS: {
          displayName: 'Удаление любых комментариев к сделкам',
          isAllowed: false,
        },
      },
    },
  },
};

const DEBTS_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'Задолженности',
  description: 'Дебиторская и кредиторская задолженности. Рассылки должникам.',
  enabled: false,
  dependencies: [],
  sections: {
    DEBTS: {
      displayName: 'Задолженности',
      isAdmin: false,
      tabs: {
        DEBTORS: {
          displayName: 'Должники',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        CREDIT: {
          displayName: 'Кредиторская задолженность',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        DEBIT: {
          displayName: 'Дебиторская задолженность',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        BILL_REGISTRY: {
          displayName: 'Счета',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        AUTOCHECK_BILLS: {
          displayName: 'Автосверка счетов',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        MAILING: {
          displayName: 'Рассылки',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        CASH_GAP: {
          displayName: 'Анализ должников',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        CREDIT_DEBIT_EXCLUSION: {
          displayName: 'Исключения',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        DEBT_COLLECTION: {
          displayName: 'Взыскание долга',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        INTEGRATION_EMAIL: {
          displayName: 'Интеграция с почтовыми серверами',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        INTEGRATION_MAIN_SMS: {
          displayName: 'Интеграция с "MainSMS"',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        DEBT_COLLECTION_READ_ALL_TASKS: {
          displayName: 'Доступ ко всем заявкам на взыскание задолженности',
          isAllowed: false,
        },
      },
    },
  },
};

/** "Project" module reference object */
const PROJECT_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'Задачи',
  description: 'Управление задачами',
  enabled: false,
  dependencies: [],
  sections: {
    PROJECT: {
      displayName: 'Задачи',
      isAdmin: false,
      tabs: {
        PROJECT: {
          displayName: 'Задачи',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        EVENT_CALENDAR: {
          displayName: 'Календарь событий',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        TIME_REPORT: {
          displayName: 'Трудозатраты',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        TEMPLATE: {
          displayName: 'Шаблоны',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        CAN_READ_ALL_PROJECTS: {
          displayName: 'Доступ ко всем задачам',
          isAllowed: false,
        },
        DELETE_OTHER_USER_COMMENTS: {
          displayName: 'Удаление любых комментариев к задачам',
          isAllowed: false,
        },
      },
    },
  },
};

/** "FDO" module reference object */
const FDO_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'ОФД',
  description: 'Работа с Операторами Фискальных Данных (ОФД)',
  enabled: false,
  dependencies: [],
  sections: {
    FDO: {
      displayName: 'ОФД',
      isAdmin: false,
      tabs: {
        KKT: {
          displayName: 'Реестр ККТ',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        RECEIPT: {
          displayName: 'Чеки',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        KKT_ACTIVITY: {
          displayName: 'Активность ККТ',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        ACTIVITY: {
          displayName: 'Виды деятельности',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {},
    },
  },
};

/** "HelpDesk" module reference object */
const HELPDESK_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'HelpDesk',
  description: 'Модуль для технической поддержка клиентов',
  enabled: false,
  dependencies: [],
  sections: {
    HELPDESK: {
      displayName: 'HelpDesk',
      isAdmin: false,
      tabs: {
        TASK: {
          displayName: 'Заявки',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        TASK_TYPE: {
          displayName: 'Типы заявок',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        TEMPLATE: {
          displayName: 'Шаблоны',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        EMAIL_SETTINGS: {
          displayName: 'Настройка почты',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        FIELDS_SETTINGS: {
          displayName: 'Настройка обязательных полей',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        READ_ALL_TASKS: {
          displayName: 'Просмотр любых заявок HelpDesk',
          isAllowed: false,
        },
        EDIT_OTHER_USERS_TASKS: {
          displayName: 'Редактирование чужих заявок HelpDesk',
          isAllowed: false,
        },
        DELETE_TASKS: {
          displayName: 'Удаление заявок HelpDesk',
          isAllowed: false,
        },
        DELETE_OTHER_USER_COMMENTS: {
          displayName: 'Удаление любых комментариев к заявкам',
          isAllowed: false,
        },
        ANSWER_WITH_AI: {
          displayName: 'Ответ на заявку HelpDesk с помощью ассистента ИИ',
          isAllowed: false,
        },
        DETERMINE_BEST_ANSWER: {
          displayName: 'Определение "Лучшего ответа" в комментариях заявки HelpDesk',
          isAllowed: false,
        },
        MERGE_HD_TASKS: {
          displayName: 'Перенос заявок HelpDesk',
          isAllowed: false,
        },
      },
    },
  },
};

/** "Traffic" module reference object */
const TRAFFIC_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'Трафик',
  description: 'Работа с трафиком',
  enabled: false,
  dependencies: [],
  sections: {
    TRAFFIC: {
      displayName: 'Трафик',
      isAdmin: false,
      tabs: {
        ADAPTER: {
          displayName: 'Оборудование',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        TRAFFIC_REPORT: {
          displayName: 'Трафик',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {},
    },
  },
};

/** "PHYSICAL_ACCESS_CONTROL" module reference */
const PHYSICAL_ACCESS_CONTROL_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'Пропуск',
  description: 'Паркинг и СКУД-системы',
  enabled: false,
  dependencies: [],
  sections: {
    PACS: {
      displayName: 'Пропуска',
      isAdmin: false,
      tabs: {
        EQUIPMENT: {
          displayName: 'Оборудование',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        EVENT_REPORT: {
          displayName: 'События',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        COMING_GOING: {
          displayName: 'Приход-уход',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        PHOTO_VERIFICATION: {
          displayName: 'Фото-верификация',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        ACCESS_CARD_CONTROL: {
          displayName: 'Контроль пропусков',
          editable: true,
          enabled: false,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        ACCESS_CARD: {
          displayName: 'Карты доступа',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        PASSAGE_TASK: {
          displayName: 'Гости',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        PASSAGE_ORDER: {
          displayName: 'Заказы пропусков',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        OPEN_DOORS: {
          displayName: 'Может открывать двери через ФРМ',
          isAllowed: false,
        },
        SYNCHRONIZE_CONVERTERS: {
          displayName: 'Может запускать синхронизацию устройств',
          isAllowed: false,
        },
        ACCESS_TO_ALL_ROOMS: {
          displayName: 'Доступ ко всем помещениям',
          isAllowed: false,
        },
        EXTERNAL_USER_BLOCK_PACS_ACCESS: {
          displayName: 'Блокировка доступа по СКУД для внешних пользователей',
          isAllowed: false,
        },
      },
    },
    PARKING: {
      displayName: 'Паркинг',
      isAdmin: false,
      tabs: {
        SETTINGS: {
          displayName: 'Настройки',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        CAR_CATEGORY: {
          displayName: 'Категории ТС',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.READ,
          permissions: {},
        },
        TRANSIT_TASK: {
          displayName: 'Гостевые заявки',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        RESIDENT_CAR: {
          displayName: 'Постоянные ТС',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        HIKVISION_CAMERA: {
          displayName: 'Камеры "Hikvision"',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
        REPORTS: {
          displayName: 'Отчеты',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {
        ACCESS_TO_ALL_TRANSIT_TASK: {
          displayName: 'Доступ ко всем заявкам на проезд',
          isAllowed: false,
        },
        MANUAL_OPEN: {
          displayName: 'Может открывать шлагбаум вручную',
          isAllowed: false,
        },
        EDIT_EVENT: {
          displayName: 'Может редактировать события камеры',
          isAllowed: false,
        },
        CAN_BLOCK_PARKING: {
          displayName: 'Может блокировать ТС клиенту',
          isAllowed: false,
        },
        CAN_DELETE_ACTIVE_TRANSIT_TASK: {
          displayName: 'Может удалять активные гостевые заявки',
          isAllowed: false,
        },
        CAN_UNLINK_TRANSIT_TASK_EVENT: {
          displayName: 'Может отвязывать события от гостевых заявок',
          isAllowed: false,
        },
        ACCESS_TO_ALL_ROOMS: {
          displayName: 'Доступ ко всем помещениям',
          isAllowed: false,
        },
      },
    },
  },
};

const FRM_AI_MODULE_REFERENCE: ApplicationModuleFullFindInterface = {
  displayName: 'ФРМ AI',
  description: 'Учебные материалы и ассистенты ИИ',
  enabled: false,
  dependencies: [],
  sections: {
    MANUAL: {
      displayName: 'Курсы',
      isAdmin: false,
      tabs: {
        COURSE: {
          displayName: 'Курсы',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {},
    },
    FRM_AI_CHAT: {
      displayName: 'ФРМ AI чат',
      isAdmin: false,
      tabs: {
        FRM_AI_CHAT: {
          displayName: 'Чат',
          editable: false,
          enabled: true,
          accessRange: [AccessLevelEnum.FORBIDDEN, AccessLevelEnum.READ, AccessLevelEnum.WRITE],
          accessLevel: AccessLevelEnum.FORBIDDEN,
          permissions: {},
        },
      },
      permissions: {},
    },
  },
};

/** Full reference access control object. Access control settings for role and user are all based on this object. */
export const APPLICATION_ACCESS_CONTROL_FULL_REFERENCE: ApplicationAccessControlFullFindInterface = {
  BASIS: BASIS_MODULE_REFERENCE,
  FINANCE: FINANCE_MODULE_REFERENCE,
  BRS: BRS_MODULE_REFERENCE,
  BUSINESS: BUSINESS_MODULE_REFERENCE,
  DEBTS: DEBTS_MODULE_REFERENCE,
  PROJECT: PROJECT_MODULE_REFERENCE,
  FDO: FDO_MODULE_REFERENCE,
  HELPDESK: HELPDESK_MODULE_REFERENCE,
  PHYSICAL_ACCESS_CONTROL: PHYSICAL_ACCESS_CONTROL_MODULE_REFERENCE,
  TRAFFIC: TRAFFIC_MODULE_REFERENCE,
  FRM_AI: FRM_AI_MODULE_REFERENCE,
};

/** Rate plan reference access control object. Access control settings for rate plan are all based on this object. */
export const APPLICATION_ACCESS_CONTROL_RATE_PLAN_REFERENCE = ((): ApplicationAccessControlRatePlanFindInterface => {
  const copy: ApplicationAccessControlFullFindInterface = JSON.parse(JSON.stringify(APPLICATION_ACCESS_CONTROL_FULL_REFERENCE));

  for (const module of Object.values(copy)) {
    for (const section of Object.values(module.sections)) {
      delete section.permissions;

      for (const tab of Object.values(section.tabs)) {
        delete tab.accessLevel;
      }
    }
  }

  return copy;
})();

/** Lightweight application modules dictionary */
export const APPLICATION_MODULES: Record<string, ApplicationModuleBaseInterface> = Object.fromEntries(
  Object
    .entries(APPLICATION_ACCESS_CONTROL_FULL_REFERENCE)
    .map(([key, { displayName, description, dependencies }]) => [
      key,
      { displayName, description, dependencies },
    ]),
);
